<template>
    <div style="min-width:90vw; max-width:90vw">
        <v-row justify="center" style="background-color: #06599D;">
            <v-col cols="0" sm="2"></v-col>
            <v-col cols="12" sm="8">
                <v-row>
                    <ReportSelection 
                        title="Admin Reports" 
                        filter=":AdminInternal:"
                        @reportChanged="(value) => selectedReport = value"
                        @downloadOnlyChanged="(value) => downloadOnly = value">
                    </ReportSelection>
                </v-row>
                <v-row v-if="requiresQuarter">
                    <v-select class="text-white" label="Select Quarter" v-model="selectedQuarter"
                    :items="quarters" variant="outlined"></v-select>
                </v-row>
                <v-row cols="6" class="d-flex justify-center">
                    <v-col v-if="requiresStartDate" >
                        <v-date-input text-color="white" label="Select Start Date" header-color="white" base-color="white" v-model="selectedStartDate" variant="underlined" max-width="368"></v-date-input>   
                    </v-col>
                    <v-col v-if="requiresEndDate">
                        <v-date-input label="Select End Date" header-color="white" base-color="white" v-model="selectedEndDate" variant="underlined" max-width="368"></v-date-input>
                    </v-col>
                </v-row>
                <v-row>
                    <v-btn @click="getReportData" color="#1e70c7">Submit</v-btn>
                </v-row>
            </v-col>
            <v-col cols="0" sm="2"></v-col>
        </v-row>
        <v-row justify="center" style="background-color: #ffffff;" class="pb-6">
            <div class="w-100 h-100">
                <v-toolbar class="w-100" density="compact" color="#1d5895">
                    <v-spacer></v-spacer>
                    <v-btn prepend-icon="mdi-download" @click="downloadFile" aria-label="Download Report"></v-btn>
                </v-toolbar>
                <div v-if="!isCustomReport" class="w-100">
                    <v-text-field v-model="searchString" label="Filter" prepend-inner-icon="mdi-filter-outline"
                        variant="outlined" hide-details single-line></v-text-field> 
                    <v-data-table items-per-page="50" :headers="headers" :items="reportData"
                        item-key="id" :search="searchString" class="v-data-table"
                        role="table" aria-label="Admin reports data table">
                    </v-data-table>                    
                </div>
            </div>
        </v-row>
    </div>          
  </template>
  
  <script setup>
  import { ref, onMounted, computed, watch } from 'vue'
  import { useSystemStore } from '@/stores/system'
  import { useReportsStore } from '@/stores/reports'
  import { useExcelStore } from '@/stores/excel'
  import ReportSelection from '@/components/ReportSelection.vue'

  import { VDateInput } from 'vuetify/lib/labs/components.mjs'
  
  const systemStore = useSystemStore()
  const reportsStore = useReportsStore()
  const excelStore = useExcelStore()
  const requiresQuarter = ref(false)
  const selectedQuarter = ref('')
  const isCustomReport = ref(false)
  const customBreakdownReportId = 12
  const customReportIds = ref([customBreakdownReportId])
  const requiresStartDate = ref(false)
  const requiresEndDate = ref(false)
  const selectedStartDate = ref()
  const selectedEndDate = ref()
  
  const reportData = ref([])
  const searchString = ref('')
  const currentReport = ref('')
  const headers = ref([])
  const selectedReport = ref(null)
  const downloadOnly = ref(false)

  const quarters = ref([])
  
  const downloadFile = () => {
      if (reportData.value.length === 0) {
          return
      }
  
      if (customReportIds.value.indexOf(selectedReport.value.Id) === -1) {
          excelStore.generateInMemoryExcelDocumentV2(reportData.value, currentReport.value).catch(error => {
              console.log('Generate In Memory Excel Document', error)
          })
      }
      else {
          excelStore.generateInMemoryExcelCustomDocument(reportData.value, currentReport.value).catch(error => {
              console.log('Generate In Memory Excel Custom Document', error)
          })
      }
  }
  
  const getReportData = () => {
      reportData.value = []
      currentReport.value = selectedReport.value.ReportTitle
  
      if (selectedReport.value.Params.length > 0) {   
  
          selectedReport.value.Params.forEach((p) => {
                if (p.Name === 'Quarter') {
                    p.Value = selectedQuarter.value
                }   
                if (p.Name === 'StartDate') {
                    p.Value = selectedStartDate.value
                }
                if (p.Name === 'EndDate') {
                    p.Value = selectedEndDate.value
                }
                if (p.Name === 'PAMUser') {
                    if (systemStore.userDetail.userType.trim() === 'PAM') {            
                    p.Value = `${systemStore.userDetail.firstname.trim()} ${systemStore.userDetail.lastname.trim()}`
                }
              }
          })
  
      }
  
      reportData.value = []
  
      if (customReportIds.value.indexOf(selectedReport.value.Id) === -1) {
          isCustomReport.value = false
      }
      else {
          isCustomReport.value = true
      }
  
      reportsStore.getReportData(selectedReport.value, downloadOnly.value, isCustomReport.value).then(result => {
      if (result.success === true) {
          if (downloadOnly.value) {     
              excelStore.downloadInMemoryExcelDocument(result.reportArray, currentReport.value).catch(error => {
                  console.log('Download In Memory Excel Document', error)
              })                 
          }
          else {
              if (result.hasData) {
                  const jsonObject = JSON.parse(result.reportData)
                  const headings = Object.keys(jsonObject[0])
                  headers.value = []
  
                  if (customReportIds.value.indexOf(selectedReport.value.Id) === -1) {
  
                      //Invite User Function on PAM Report
                      if (selectedReport.value.Id === 10) {      
                          if (systemStore.userDetail.isOrganizationAdmin) {
                              headers.value.push({
                                  title: `Invite New User`,
                                  value: `inviteNewUser`,
                                  headerProps: { class: 'header-style' },
                                  sortable: false
                              });
                          }
                      }
  
                      headings.forEach(element => {
                          headers.value.push({
                              title: `${reportsStore.formatHeader(element)}`,
                              value: `${element}`,
                              headerProps: { class: 'header-style' },
                              sortable: true
                          });
                      });
  
                  }   
                  
                  reportData.value = jsonObject;
              }            
          }
      }
    }).catch(error => {
        console.log(`get data`, error)
    })
  }

  watch(
      () => selectedReport.value, (val) => {
          requiresQuarter.value = false
          requiresStartDate.value = false
          requiresEndDate.value = false
  
          if (val.Params.length > 0) {   
              val.Params.forEach((p) => {
                  if (p.Name === 'Quarter') {
                      requiresQuarter.value = true
                  }
                  if (p.Name === 'StartDate') {
                      requiresStartDate.value = true
                  }
                  if (p.Name === 'EndDate') {
                      requiresEndDate.value = true
                  }
              })
          } 
      }
  )
    
  const showCustomReport = computed(() => {
      return reportData.value.length > 0 && isCustomReport.value === true
  });
  
  onMounted(() => {
      systemStore.getQuarters().then(result => {
          if (result.success) {
              quarters.value = result.quarters
                  .filter(q => q.currentQuarters === true)
                  .map(q => q.quarter)
          }
      }).catch(error => {
          console.log('recalculateLocalValue', error)
      })
  })
  
  </script>
  
  <style scoped>
  
  .header-style {
  
  /* background-color: #012a4b; */
  background-color: #012a4b;
  color: white;
  }
  
  .v-data-table tbody tr:nth-child(even) {
    background-color: #e0e6ec;
    color: #202520;
  }
  
  .v-data-table tbody tr:nth-child(odd) {
    background-color: whitesmoke;
    color: #202520;
  }
  </style>